import { Action } from 'redux';

import { AdjustAuthorConfiguredDCR } from '~src/bootstrap/redux/actions/AppActionTypes';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { EntityActionNames, IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { IAuthorConfiguredDCRActions } from '~src/spacesPlatform/models/actionNames';
import { ViewContentType } from '~src/spacesPlatform/models/ContentTypes';
import { IDCRAuthorConfigInfo } from '~src/spacesPlatform/models/IDCRAuthorConfigInfo';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isWorkbookViewsSubspaceTab } from '~src/spacesPlatform/state/SpaceInfo';

import { ProcessedMonitoringStatus, ProcessedShouldMonitor } from '../types/AuthorConfiguredDCRTypes';
import { showMonitorDataChangesInViewSubspace } from '../utils/AuthorConfiguredDCRUtils';

function isSingleViewSelected(targetItems: IDCRAuthorConfigInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === ViewContentType.name;
}

function isVisible(targetItems: IDCRAuthorConfigInfo[], state: AppState): boolean {
  const spaceInfo = selectSpaceInfo(state);
  return showMonitorDataChangesInViewSubspace() && isWorkbookViewsSubspaceTab(spaceInfo) && isSingleViewSelected(targetItems);
}

export const AuthorConfiguredDCROnEntityAction: IEntityAction<IDCRAuthorConfigInfo, IAuthorConfiguredDCRActions> = {
  name: EntityActionNames.AuthorConfiguredDCROn,
  translateName: (targetItems: IDCRAuthorConfigInfo[]) => Messages.authorConfiguredDCROnOption(),
  isVisible,
  isClickable: (availableActions: IAuthorConfiguredDCRActions | undefined, targetItems: IDCRAuthorConfigInfo[]) => {
    return (
      isSingleViewSelected(targetItems) &&
      availableActions?.turnAuthorConfiguredDCROn
    );
  },
  isChecked: (availableActions: IAuthorConfiguredDCRActions, targetItems: IDCRAuthorConfigInfo[], state: AppState) => {
    return (
      isSingleViewSelected(targetItems) &&
      targetItems[0].monitorDataChangesAuthorSelection === ProcessedShouldMonitor.ShouldMonitor
    );
  },
  createReduxAction: (targetItems: IDCRAuthorConfigInfo[]) => authorConfiguredDCRForContents(targetItems, true),
};

export const AuthorConfiguredDCROffEntityAction: IEntityAction<IDCRAuthorConfigInfo, IAuthorConfiguredDCRActions> = {
  name: EntityActionNames.AuthorConfiguredDCROff,
  translateName: (targetItems: IDCRAuthorConfigInfo[]) => Messages.authorConfiguredDCROffOption(),
  isVisible,
  isClickable: (availableActions: IAuthorConfiguredDCRActions | undefined, targetItems: IDCRAuthorConfigInfo[]) => {
    return (
      isSingleViewSelected(targetItems) &&
      availableActions?.turnAuthorConfiguredDCROff
    );
  },
  isChecked: (availableActions: IAuthorConfiguredDCRActions, targetItems: IDCRAuthorConfigInfo[], state: AppState) => {
    return (
      isSingleViewSelected(targetItems) &&
      targetItems[0]?.monitorDataChangesAuthorSelection === ProcessedShouldMonitor.ShouldNotMonitor
    );
  },
  createReduxAction: (targetItems: IDCRAuthorConfigInfo[]) => authorConfiguredDCRForContents(targetItems, false)
};

export const AuthorConfiguredDCRSubmenu: IEntityAction<IDCRAuthorConfigInfo, unknown> = {
  name: EntityActionNames.AuthorConfiguredDCRSubmenu,
  translateName: Messages.authorConfiguredDCRSubmenuText,
  isVisible,
  isClickable: (availableActions: IAuthorConfiguredDCRActions | undefined, targetItems: IDCRAuthorConfigInfo[], state: AppState) => {
    return (
      isSingleViewSelected(targetItems) &&
      availableActions?.turnAuthorConfiguredDCROn &&
      availableActions?.turnAuthorConfiguredDCROff &&
      targetItems[0].monitorDataChangesStatus !== ProcessedMonitoringStatus.Unknown &&
      targetItems[0].monitorDataChangesStatus !== ProcessedMonitoringStatus.Loading
    );
  },
  createReduxAction: () => null,
};

export interface AuthorConfiguredDCRAction extends Action {
  selectedContent: IDCRAuthorConfigInfo[];
  shouldMonitor: boolean;
}

export function authorConfiguredDCRForContents(
  selectedContent: IDCRAuthorConfigInfo[],
  shouldMonitor: boolean,
): AuthorConfiguredDCRAction {
  if ((shouldMonitor && selectedContent[0].monitorDataChangesAuthorSelection === ProcessedShouldMonitor.ShouldMonitor) ||
    (!shouldMonitor && selectedContent[0].monitorDataChangesAuthorSelection === ProcessedShouldMonitor.ShouldNotMonitor)) {
    // No-op if the selection doesn't change
    return null;
  }
  return {
    type: AdjustAuthorConfiguredDCR,
    selectedContent,
    shouldMonitor,
  };
}